// ---------------------------------------------------------------------------
// authRedirect.js -- public surface for the local auto-login helpers.
//
// SECURITY: the mock-auth token format and the colon-delimited token builder
// MUST NOT ship to production. The actual token-building logic, the
// developer signing secret, and the POST to /api/Authentication/login live
// in authRedirect.local.js. That sibling file is pulled in here by a
// `require()` that is statically guarded by `process.env.NODE_ENV !==
// "production"`.
//
// Why this works:
//   * Create React App's webpack build replaces every reference to
//     `process.env.NODE_ENV` with the literal string at build time.
//   * In production builds Terser evaluates `"production" !== "production"`
//     to `false` and removes the entire branch, including the require().
//   * With no live importer, webpack tree-shakes authRedirect.local out of
//     the production chunk graph entirely. The mock-auth token shape and
//     the dev-only privilege strings never reach the production bundle.
//   * Every public function in this file checks `devModule` first and
//     short-circuits to a safe no-op when the dev module is absent, so
//     calling code that still references these symbols in a production
//     build behaves exactly as if local auto-login were disabled.
//
// The signing secret check on the server side is what actually closes the
// auth-bypass loop -- the front-end strip is a defense-in-depth measure to
// stop the bundle from advertising the token shape and credentials.
// ---------------------------------------------------------------------------

const LOCAL_HOSTNAMES = new Set(["localhost", "127.0.0.1", "::1"]);

let devModule = null;
if (process.env.NODE_ENV !== "production") {
  // eslint-disable-next-line global-require
  devModule = require("./authRedirect.local");
}

const safeHostname = (hostname) => {
  if (typeof hostname === "string") {
    return hostname;
  }
  if (typeof window !== "undefined" && window.location) {
    return window.location.hostname;
  }
  return "";
};

const safePathname = (pathname) => {
  if (typeof pathname === "string") {
    return pathname;
  }
  if (typeof window !== "undefined" && window.location) {
    return window.location.pathname;
  }
  return "";
};

export const isLocalDevelopmentHost = (hostname) =>
  LOCAL_HOSTNAMES.has(safeHostname(hostname));

// Unauthorized requests always bounce to the login page. The function is
// kept here (rather than collapsed into a string constant) so callers do
// not need to differentiate between "session expired in dev" and "session
// expired in prod" -- both go to the same place.
// eslint-disable-next-line no-unused-vars
export const getUnauthorizedRedirectPath = (hostname) => "/login";

export const isLocalAutoLoginEnabled = () => {
  if (!devModule) {
    return false;
  }
  return devModule.isLocalAutoLoginEnabled();
};

export const hasStoredLocalAutoLoginToken = () => {
  if (!devModule) {
    return false;
  }
  return devModule.hasStoredLocalAutoLoginToken();
};

export const getLocalAutoLoginToken = (pathname) => {
  if (!devModule) {
    throw new Error(
      "Local mock auth token builder is not available outside of " +
        "development. This is intentional: the token format and credentials " +
        "must not be reachable from a production build."
    );
  }
  return devModule.getLocalAutoLoginToken(safePathname(pathname));
};

export const shouldRefreshLocalAutoLogin = (hostname, pathname) => {
  if (!devModule) {
    return false;
  }
  if (!isLocalDevelopmentHost(hostname)) {
    return false;
  }

  try {
    const storedToken = window.sessionStorage.getItem(
      devModule.LOCAL_AUTH_TOKEN_STORAGE_KEY
    );
    return storedToken !== devModule.getLocalAutoLoginToken(safePathname(pathname));
  } catch (error) {
    return true;
  }
};

// Returns an axios request config object containing the X-Dev-Mock-Auth-Secret
// header when running on a local development host with the secret configured.
// Returns an empty object (no extra headers) in production or when the secret
// is not set, so callers can pass the result directly to axiosPost without
// any additional guarding.
export const getLocalDevAuthRequestOptions = (hostname) => {
  if (!devModule) {
    return {};
  }
  if (!isLocalDevelopmentHost(hostname)) {
    return {};
  }
  return devModule.getLocalDevAuthRequestOptions();
};

export const performLocalAutoLogin = async (hostname, pathname) => {
  if (!devModule) {
    return null;
  }
  if (!isLocalDevelopmentHost(hostname) || !devModule.isLocalAutoLoginEnabled()) {
    return null;
  }

  return devModule.performLocalAutoLogin({
    pathname: safePathname(pathname),
  });
};
